// API Base Configuration
// Use relative path for development to work with Vite proxy
// In production, use the same host as the frontend
const getApiBaseUrl = () => {
  if (import.meta.env.DEV) {
    return ''; // Use Vite proxy in development
  }
  // In production, use empty string since endpoints already include /api prefix
  // ALB routes /api/* directly to backend
  return '';
};

const API_BASE_URL = getApiBaseUrl()

// Helper function to get current user ID from localStorage
const getCurrentUserId = (): string | null => {
  try {
    const userStr = localStorage.getItem('currentUser');
    if (userStr) {
      const user = JSON.parse(userStr);
      return user.id || null;
    }
  } catch (error) {
    console.error('Error getting current user ID:', error);
  }
  return null;
};

// HTTP Client utility
class ApiClient {
  private baseURL: string

  constructor(baseURL: string) {
    this.baseURL = baseURL
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseURL}${endpoint}`
    
    // Get current user ID and add to headers
    const currentUserId = getCurrentUserId();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    };
    
    // Add X-User-Id header if user is logged in
    if (currentUserId) {
      headers['X-User-Id'] = currentUserId;
    }
    
    const config: RequestInit = {
      ...options,
      headers,
    }

    try {
      const response = await fetch(url, config)
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      
      const data = await response.json()
      return data
    } catch (error) {
      console.error('API request failed:', error)
      throw error
    }
  }

  async get<T>(endpoint: string, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'GET' })
  }

  async post<T>(endpoint: string, data?: any, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    })
  }

  async put<T>(endpoint: string, data?: any, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    })
  }

  async patch<T>(endpoint: string, data?: any, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    })
  }

  async delete<T>(endpoint: string, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' })
  }
}

export const apiClient = new ApiClient(API_BASE_URL)

// Export helper functions for external use
export { getCurrentUserId }

// Wrapper for direct fetch calls to include X-User-Id header
export const fetchWithAuth = async (url: string, options: RequestInit = {}): Promise<Response> => {
  const currentUserId = getCurrentUserId();
  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string>),
  };
  
  // Add X-User-Id header if user is logged in
  if (currentUserId) {
    headers['X-User-Id'] = currentUserId;
  }
  
  return fetch(url, {
    ...options,
    headers,
  });
};